import React from 'react';
import {cn} from '@/lib/utils';
import {Badge} from '@/components/ui/badge';
import {Avatar, AvatarFallback} from '@/components/ui/avatar';
import {Separator} from '@/components/ui/separator';
import {Tabs, TabsList, TabsTrigger} from '@/components/ui/tabs';
import {Target, ArrowUp, TrendingUp, Clock, Info} from 'lucide-react';
import {formatPremium, useRelativeTime} from './data';
import type {AgentData, MetricType, TimePeriodType} from './data';

export type {MetricType, TimePeriodType};

interface LeaderboardPopoutProps {
    metric: MetricType;
    setMetric: (m: MetricType) => void;
    timePeriod: TimePeriodType;
    setTimePeriod: (t: TimePeriodType) => void;
    agents: AgentData[];
    currentAgent: AgentData | null;
    currentUserName: string | null;
    isLoading?: boolean;
    updatedAt?: number;
}

/* ─── Medal styles for top 3 ─── */

const MEDAL_STYLES: Record<
    number,
    {accentColor: string; bgTint: string; premiumColor: string; badgeBorder: string; badgeBg: string; badgeText: string}
> = {
    1: {
        accentColor: 'bg-amber-500',
        bgTint: 'bg-amber-100/80',
        premiumColor: 'text-amber-800',
        badgeBorder: 'border-amber-500',
        badgeBg: 'bg-amber-100',
        badgeText: 'text-amber-800',
    },
    2: {
        accentColor: 'bg-slate-500',
        bgTint: 'bg-slate-200/60',
        premiumColor: 'text-slate-700',
        badgeBorder: 'border-slate-500',
        badgeBg: 'bg-slate-200',
        badgeText: 'text-slate-700',
    },
    3: {
        accentColor: 'bg-amber-700',
        bgTint: 'bg-orange-100/60',
        premiumColor: 'text-amber-900',
        badgeBorder: 'border-amber-700/80',
        badgeBg: 'bg-amber-100/80',
        badgeText: 'text-amber-900',
    },
};

/* ─── Deals Pill ─── */

function DealsPill({deals, className}: {deals: number; className?: string}) {
    if (deals <= 0) return null;
    return (
        <span
            className={cn(
                'shrink-0 rounded-full border border-stone-200/60 bg-stone-100/60 px-1.5 py-0 text-[9px] text-stone-500',
                className,
            )}
            style={{fontFamily: 'FredokaMedium, sans-serif'}}
        >
            {deals} {deals === 1 ? 'deal' : 'deals'}
        </span>
    );
}

/* ─── Compact Row ─── */

function CompactRow({
    agent,
    variant,
    index,
    isCurrentUser,
}: {
    agent: AgentData;
    variant: 'gold' | 'silver' | 'bronze' | 'default';
    index?: number;
    isCurrentUser?: boolean;
}) {
    const isMedal = variant !== 'default';
    const medal = isMedal ? MEDAL_STYLES[agent.rank] : null;

    // Fading sage accent for ranks 4+
    const sageOpacity = index !== undefined ? 1 - index * 0.08 : 1;

    return (
        <div
            className={cn(
                'group flex items-center gap-2.5 rounded-lg px-2.5 py-[7px] transition-colors duration-150',
                'hover:bg-stone-100/40',
                medal?.bgTint,
                isCurrentUser && 'bg-[#456d69]/[0.06] ring-1 ring-[#456d69]/20',
            )}
        >
            {/* Left accent bar */}
            <div
                className={cn(
                    'h-5 w-[3px] shrink-0 rounded-full',
                    isCurrentUser ? 'bg-[#456d69]' : medal ? medal.accentColor : 'bg-[#456d69]',
                )}
                style={!medal && !isCurrentUser ? {opacity: sageOpacity} : undefined}
            />

            {/* Rank */}
            <Badge
                variant="outline"
                className={cn(
                    'h-6 w-6 shrink-0 items-center justify-center rounded-full px-0',
                    isCurrentUser
                        ? 'border-[1.5px] border-[#456d69]/60 bg-[#e8edec] text-[#456d69]'
                        : isMedal
                          ? `border-2 ${medal!.badgeBorder} ${medal!.badgeBg} ${medal!.badgeText}`
                          : 'border-[1.5px] border-[#456d69]/60 bg-[#e8edec]/50 text-[#456d69]',
                )}
                style={{fontFamily: 'FredokaBold, sans-serif', fontSize: '10px'}}
            >
                {agent.rank}
            </Badge>

            {/* Avatar */}
            <Avatar className="h-6 w-6 shrink-0">
                <AvatarFallback
                    className={cn(
                        'border text-stone-500',
                        isCurrentUser
                            ? 'border-[#456d69]/40 bg-[#e8edec] text-[#456d69]'
                            : 'border-stone-200 bg-stone-50',
                    )}
                    style={{fontFamily: 'FredokaSemiBold, sans-serif', fontSize: '8px'}}
                >
                    {agent.initials}
                </AvatarFallback>
            </Avatar>

            {/* Name + You badge */}
            <div className="flex min-w-0 items-center gap-1.5">
                <span
                    className={cn('truncate text-xs', isCurrentUser ? 'text-[#456d69]' : 'text-stone-700')}
                    style={{fontFamily: isCurrentUser ? 'FredokaSemiBold, sans-serif' : 'FredokaMedium, sans-serif'}}
                >
                    {agent.name}
                </span>
                {isCurrentUser && (
                    <Badge
                        className="shrink-0 border-[#456d69]/30 bg-[#e8edec] px-1.5 py-0 text-[9px] text-[#456d69] hover:bg-[#e8edec]"
                        style={{fontFamily: 'FredokaSemiBold, sans-serif'}}
                    >
                        You
                    </Badge>
                )}
            </div>

            {/* Deals pill */}
            <DealsPill deals={agent.deals} />

            {/* Premium */}
            <span
                className={cn('ml-auto shrink-0 text-sm', medal?.premiumColor || 'text-stone-900')}
                style={{fontFamily: 'LoraSemiBold, serif', fontVariantNumeric: 'tabular-nums'}}
            >
                {formatPremium(agent.premium)}
            </span>
        </div>
    );
}

/* ─── Current Agent Row (for rank > 10) ─── */

function CompactCurrentRow({agent, topAgents}: {agent: AgentData; topAgents: AgentData[]}) {
    const lastInList = topAgents.length > 0 ? topAgents[topAgents.length - 1] : null;
    const gapToList = lastInList ? lastInList.premium - agent.premium : 0;

    return (
        <div
            className="relative overflow-hidden rounded-lg border-[1.5px] border-[#456d69]/40 px-2.5 py-2"
            style={{
                background:
                    'linear-gradient(135deg, rgba(232,237,236,0.8) 0%, rgba(69,109,105,0.06) 50%, rgba(232,237,236,0.6) 100%)',
            }}
        >
            {/* Left accent */}
            <div className="absolute inset-y-0 left-0 w-[3px] bg-gradient-to-b from-[#456d69] to-[#5a8a85]" />

            <div className="flex items-center gap-2.5 pl-1">
                {/* Rank */}
                <Badge
                    variant="outline"
                    className="h-6 w-6 shrink-0 items-center justify-center rounded-full border-[1.5px] border-[#456d69] bg-[#e8edec] px-0 text-[#456d69]"
                    style={{fontFamily: 'FredokaBold, sans-serif', fontSize: '10px'}}
                >
                    {agent.rank}
                </Badge>

                {/* Avatar */}
                <Avatar className="h-6 w-6 shrink-0">
                    <AvatarFallback
                        className="border border-[#456d69]/40 bg-[#e8edec] text-[#456d69]"
                        style={{fontFamily: 'FredokaSemiBold, sans-serif', fontSize: '8px'}}
                    >
                        {agent.initials}
                    </AvatarFallback>
                </Avatar>

                {/* Name + badge */}
                <div className="flex min-w-0 items-center gap-1.5">
                    <span
                        className="truncate text-xs text-[#456d69]"
                        style={{fontFamily: 'FredokaSemiBold, sans-serif'}}
                    >
                        {agent.name}
                    </span>
                    <Badge
                        className="shrink-0 border-[#456d69]/30 bg-[#e8edec] px-1.5 py-0 text-[9px] text-[#456d69] hover:bg-[#e8edec]"
                        style={{fontFamily: 'FredokaSemiBold, sans-serif'}}
                    >
                        You
                    </Badge>
                </div>

                {/* Deals pill */}
                <DealsPill deals={agent.deals} className="border-[#456d69]/20 bg-[#e8edec]/60 text-[#456d69]" />

                <div className="flex-1" />

                {/* Gap to top of list */}
                {gapToList > 0 && (
                    <div className="flex items-center gap-1 text-[#456d69]/70">
                        <TrendingUp className="h-3 w-3" />
                        <span
                            className="text-[10px] whitespace-nowrap"
                            style={{fontFamily: 'FredokaMedium, sans-serif'}}
                        >
                            {formatPremium(gapToList)} to top {topAgents.length}
                        </span>
                    </div>
                )}

                {/* Premium */}
                <div className="flex shrink-0 items-center gap-1">
                    <ArrowUp className="h-3 w-3 text-[#456d69]" />
                    <span
                        className="text-sm text-stone-900"
                        style={{fontFamily: 'LoraSemiBold, serif', fontVariantNumeric: 'tabular-nums'}}
                    >
                        {formatPremium(agent.premium)}
                    </span>
                </div>
            </div>
        </div>
    );
}

/* ─── Skeleton rows ─── */

function SkeletonRows() {
    return (
        <div className="flex flex-col gap-1 px-2 py-2">
            {Array.from({length: 6}).map((_, i) => (
                <div key={i} className="flex items-center gap-2.5 rounded-lg px-2.5 py-[7px]">
                    <div className="h-5 w-[3px] shrink-0 animate-pulse rounded-full bg-stone-200" />
                    <div className="h-6 w-6 shrink-0 animate-pulse rounded-full bg-stone-200" />
                    <div className="h-6 w-6 shrink-0 animate-pulse rounded-full bg-stone-200" />
                    <div className="h-3 flex-1 animate-pulse rounded bg-stone-200" />
                    <div className="h-4 w-14 shrink-0 animate-pulse rounded bg-stone-200" />
                </div>
            ))}
        </div>
    );
}

/* ─── Empty state ─── */

function EmptyState() {
    return (
        <div className="flex flex-col items-center justify-center py-10">
            <span className="text-xs text-stone-400" style={{fontFamily: 'FredokaMedium, sans-serif'}}>
                No activity yet
            </span>
        </div>
    );
}

/* ─── Main Popout Content ─── */

export default function LeaderboardPopout({
    metric,
    setMetric,
    timePeriod,
    setTimePeriod,
    agents,
    currentAgent,
    currentUserName,
    isLoading,
    updatedAt,
}: LeaderboardPopoutProps) {
    const topThree = agents.slice(0, 3);
    const restAgents = agents.slice(3);
    const hasData = agents.length > 0;
    const showCurrentAgent = currentAgent !== null;
    const relativeTime = useRelativeTime(updatedAt);

    // Check if a given agent is the current user (for inline "You" badge)
    const isCurrentUser = (agent: AgentData) => {
        if (!currentUserName) return false;
        // Only highlight inline if they're in the top 10 (currentAgent is null when in top 10)
        if (currentAgent) return false;
        return agent.name.toLowerCase() === currentUserName.toLowerCase();
    };

    return (
        <div className="flex flex-col">
            {/* ── Header ── */}
            <div className="px-4 pt-4 pb-2">
                {/* Title row */}
                <div className="mb-2.5 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <div className="flex h-6 w-6 items-center justify-center rounded-md bg-[#456d69] shadow-sm">
                            <Target className="h-3 w-3 text-white" />
                        </div>
                        <h2
                            className="text-sm tracking-tight text-stone-900"
                            style={{fontFamily: 'LoraSemiBold, serif'}}
                        >
                            Sales Leaderboard
                        </h2>
                    </div>

                    {/* Updated time + info hover */}
                    {updatedAt != null && (
                        <div className="flex items-center gap-1.5 text-stone-400">
                            <Clock className="h-3 w-3" />
                            <span className="text-[10px]" style={{fontFamily: 'FredokaMedium, sans-serif'}}>
                                {relativeTime}
                            </span>
                            <div className="group relative">
                                <button
                                    type="button"
                                    className="rounded-full p-0.5 text-stone-400 transition-colors hover:text-stone-600"
                                    aria-label="Data refresh information"
                                    aria-describedby="popout-info-tooltip"
                                >
                                    <Info className="h-3 w-3" />
                                </button>
                                <div
                                    id="popout-info-tooltip"
                                    role="tooltip"
                                    className="invisible absolute right-0 top-full z-50 mt-1 w-48 rounded-lg border border-stone-200 bg-white px-3 py-2 text-[11px] leading-relaxed text-stone-600 shadow-md group-hover:visible group-focus-within:visible"
                                    style={{fontFamily: 'FredokaMedium, sans-serif'}}
                                >
                                    Data is refreshed every ~10 minutes, Monday through Friday.
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Toggle row */}
                <div className="flex items-center gap-2">
                    {/* Metric toggle */}
                    <Tabs value={metric} onValueChange={(v) => setMetric(v as MetricType)}>
                        <TabsList className="h-7 rounded-md border border-[#456d69]/20 bg-[#e8edec]/50 p-0.5">
                            <TabsTrigger
                                value="submitted"
                                className="rounded-[5px] px-2 py-0.5 text-[11px] text-[#456d69]/60 data-[state=active]:bg-[#456d69] data-[state=active]:text-white data-[state=active]:shadow-sm"
                                style={{fontFamily: 'FredokaSemiBold, sans-serif'}}
                            >
                                New Deal
                            </TabsTrigger>
                            <TabsTrigger
                                value="delivered"
                                className="rounded-[5px] px-2 py-0.5 text-[11px] text-[#456d69]/60 data-[state=active]:bg-[#456d69] data-[state=active]:text-white data-[state=active]:shadow-sm"
                                style={{fontFamily: 'FredokaSemiBold, sans-serif'}}
                            >
                                Delivered
                            </TabsTrigger>
                        </TabsList>
                    </Tabs>

                    {/* Divider */}
                    <div className="h-4 w-px bg-stone-300/40" />

                    {/* Time period toggle */}
                    <Tabs value={timePeriod} onValueChange={(v) => setTimePeriod(v as TimePeriodType)}>
                        <TabsList className="h-7 rounded-md border border-stone-200/60 bg-white/50 p-0.5">
                            <TabsTrigger
                                value="daily"
                                className="rounded-[5px] px-2 py-0.5 text-[11px] data-[state=active]:bg-stone-800 data-[state=active]:text-white data-[state=active]:shadow-sm"
                                style={{fontFamily: 'FredokaSemiBold, sans-serif'}}
                            >
                                D
                            </TabsTrigger>
                            <TabsTrigger
                                value="weekly"
                                className="rounded-[5px] px-2 py-0.5 text-[11px] data-[state=active]:bg-stone-800 data-[state=active]:text-white data-[state=active]:shadow-sm"
                                style={{fontFamily: 'FredokaSemiBold, sans-serif'}}
                            >
                                W
                            </TabsTrigger>
                            <TabsTrigger
                                value="monthly"
                                className="rounded-[5px] px-2 py-0.5 text-[11px] data-[state=active]:bg-stone-800 data-[state=active]:text-white data-[state=active]:shadow-sm"
                                style={{fontFamily: 'FredokaSemiBold, sans-serif'}}
                            >
                                M
                            </TabsTrigger>
                        </TabsList>
                    </Tabs>
                </div>
            </div>

            <Separator className="bg-stone-200/50" />

            {/* ── Loading state ── */}
            {isLoading && !hasData && <SkeletonRows />}

            {/* ── Empty state ── */}
            {!isLoading && !hasData && <EmptyState />}

            {/* ── Content ── */}
            {hasData && (
                <>
                    {/* ── Top 3 Zone ── */}
                    {topThree.length > 0 && (
                        <div
                            className="px-2 pt-1.5 pb-1"
                            style={{
                                background:
                                    'linear-gradient(180deg, rgba(251,191,36,0.08) 0%, rgba(251,191,36,0.02) 100%)',
                            }}
                        >
                            {topThree.map((agent) => (
                                <CompactRow
                                    key={agent.rank}
                                    agent={agent}
                                    variant={agent.rank === 1 ? 'gold' : agent.rank === 2 ? 'silver' : 'bronze'}
                                    isCurrentUser={isCurrentUser(agent)}
                                />
                            ))}
                        </div>
                    )}

                    {restAgents.length > 0 && (
                        <>
                            <div className="px-4">
                                <Separator className="bg-stone-200/40" />
                            </div>

                            {/* ── Remaining ranks ── */}
                            <div className="px-2 pt-1 pb-1.5">
                                {restAgents.map((agent, index) => (
                                    <CompactRow
                                        key={agent.rank}
                                        agent={agent}
                                        variant="default"
                                        index={index}
                                        isCurrentUser={isCurrentUser(agent)}
                                    />
                                ))}
                            </div>
                        </>
                    )}

                    {/* ── Your Position (only when rank > 10) ── */}
                    {showCurrentAgent && (
                        <>
                            <div className="px-4">
                                <Separator className="bg-stone-200/40" />
                            </div>

                            <div className="px-3 pt-2 pb-3">
                                <div className="mb-1.5 flex items-center gap-2">
                                    <span
                                        className="text-[10px] text-stone-400"
                                        style={{
                                            fontFamily: 'FredokaSemiBold, sans-serif',
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.08em',
                                        }}
                                    >
                                        Your Position
                                    </span>
                                    <Separator className="flex-1 bg-stone-200/40" />
                                </div>
                                <CompactCurrentRow agent={currentAgent} topAgents={agents} />
                            </div>
                        </>
                    )}
                </>
            )}
        </div>
    );
}
